import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/store'
import { placeStorefrontOrder, startStorefrontCheckout } from '@/features/marketplace/api'
import { cartSubtotal, cartTax, useCart } from '@/features/marketplace/cart'
import { quoteShipping, useStorefrontSettings } from '@/features/marketplace/useStorefront'
import { storefrontAuthMessage } from '@/routes/storefront/authMessage'
import { toast } from '@/lib/toast'

/** The handful of dial codes the storefront actually ships to; see `_COUNTRY_CODES` on the API. */
const DIAL_CODES = [
  { code: '+91', label: '+91 (India)' },
  { code: '+1', label: '+1 (US/Canada)' },
  { code: '+44', label: '+44 (UK)' },
  { code: '+61', label: '+61 (Australia)' },
  { code: '+971', label: '+971 (UAE)' },
  { code: '+65', label: '+65 (Singapore)' },
] as const

/** Mirrors `ShippingAddressInput` on the API, field for field and limit for limit. */
const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name for the delivery').max(255),
  dial_code: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{0,3}$/, 'Pick a dial code'),
  // Digits only, no spaces or symbols. Required: the courier calls it on the way.
  phone: z
    .string()
    .trim()
    .min(1, 'Enter a phone number for the courier')
    .regex(/^\d{6,14}$/, 'Enter digits only, 6-14 digits, no spaces or symbols'),
  line1: z.string().trim().min(1, 'Enter the street address').max(255),
  line2: z.string().trim().max(255).optional(),
  city: z.string().trim().min(1, 'Enter the town or city').max(100),
  state: z.string().trim().min(1, 'Enter the state').max(50),
  postal_code: z
    .string()
    .trim()
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9 -]{1,8}[A-Za-z0-9]$/,
      'Postcode must be 3-10 letters, digits, spaces or hyphens',
    ),
  country: z.string().trim().min(1, 'Enter the country').max(100),
  notes: z.string().trim().max(1000, 'Keep delivery notes under 1000 characters').optional(),
})

type FormValues = z.infer<typeof schema>

/**
 * The products a refused checkout named as no longer for sale.
 *
 * `ITEMS_UNAVAILABLE` carries them in `details.product_ids`, and the message the shopper
 * reads says they have been taken out of the basket -- so they have to actually be taken
 * out. Anything else, including a differently-shaped payload from an older API, removes
 * nothing: emptying somebody's basket on an error we did not recognise is worse than
 * leaving it alone.
 */
function withdrawnProductIds(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== 'ITEMS_UNAVAILABLE') return []
  const ids = (error.details as { product_ids?: unknown } | undefined)?.product_ids
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
}

/**
 * Split a stored `+9198765432101` back into the dial code and the digits the phone
 * input expects. Falls back to the default dial code when the stored value does not
 * start with one of the ones this shop offers, or is missing.
 */
function splitStoredPhone(raw: string | null | undefined): { dial_code: string; phone: string } {
  const value = (raw ?? '').trim()
  const match = DIAL_CODES.find((d) => value.startsWith(d.code))
  if (match) return { dial_code: match.code, phone: value.slice(match.code.length) }
  const digits = value.replace(/\D/g, '')
  return { dial_code: '+91', phone: digits }
}

/**
 * Address, then payment (M24-04).
 *
 * The order is created **before** the card is charged, not after, and that ordering is the
 * whole design. `POST /marketplace/orders` prices the basket server-side and returns a
 * draft carrying `amount_due`; `POST /orders/{id}/checkout` then raises a hosted card page
 * for exactly that figure. If the shopper abandons the card page, the order simply sits
 * unpaid in their history with a "Pay now" button on it -- nothing is lost, and no money
 * has moved against an order that does not exist.
 *
 * The basket is emptied once the order exists, not once it is paid. The order is the record
 * from that moment, and leaving the basket full would let a distracted shopper place the
 * same order twice.
 */
export function StorefrontCheckout() {
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const lines = useCart((s) => s.lines)
  const clear = useCart((s) => s.clear)
  const remove = useCart((s) => s.remove)
  const navigate = useNavigate()
  const location = useLocation()
  const { settings, money, isLoading: settingsLoading } = useStorefrontSettings()
  const [formError, setFormError] = useState<string | null>(null)
  // Emptying the basket is the last thing a successful checkout does, and the empty basket
  // is what the guard below bounces on -- so this screen has to know an order already
  // exists. A ref rather than state on purpose: emptying the basket is a `useSyncExternalStore`
  // change, which React re-renders at sync priority without picking up a `setState` queued in
  // the same async continuation. The render would still read `false` and bounce the shopper
  // back to an empty basket having just placed a real order. A ref is read as written.
  const placed = useRef(false)
  // The browser's location, when the shopper chose to share it. It only decides which of
  // our sites the parcel leaves from (the nearest one holding everything); without it the
  // server goes by the postcode.
  const [pin, setPin] = useState<{ latitude: number; longitude: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [locateError, setLocateError] = useState<string | null>(null)

  const shareLocation = () => {
    if (!navigator.geolocation) {
      setLocateError('This browser cannot share a location. We will go by your postcode.')
      return
    }
    setLocating(true)
    setLocateError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPin({ latitude: position.coords.latitude, longitude: position.coords.longitude })
        setLocating(false)
      },
      () => {
        setLocateError('Location was not shared. We will go by your postcode.')
        setLocating(false)
      },
      { timeout: 10000, maximumAge: 300000 },
    )
  }

  const goods = cartSubtotal(lines)
  const tax = cartTax(lines)
  const shipping = quoteShipping(settings, goods + tax)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: user?.full_name ?? '',
      ...splitStoredPhone(user?.phone),
      country: 'India',
    },
    // A garbage phone number should stop looking wrong the moment it is fixed, not just the
    // moment the form is submitted -- validating on submit alone leaves a shopper staring at
    // an error that only clears on the next click.
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  if (status === 'loading' || settingsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  // The account wall. `from` so signing in lands back here rather than on the shop front.
  if (status !== 'authenticated') {
    return <Navigate to="/store/login" state={{ from: location.pathname }} replace />
  }

  if (lines.length === 0 && !placed.current) return <Navigate to="/store/cart" replace />

  const onSubmit = handleSubmit(async ({ notes, ...address }) => {
    setFormError(null)
    let orderId: string
    let orderNumber: string
    try {
      const order = await placeStorefrontOrder({
        items: lines.map((line) => ({ product_id: line.product_id, quantity: line.quantity })),
        shipping_address: {
          ...address,
          phone: address.phone?.trim() || null,
          // A dial code with no phone number is noise, not an address detail.
          dial_code: address.phone?.trim() ? address.dial_code : undefined,
          line2: address.line2?.trim() || null,
          state: address.state?.trim() || null,
          ...(pin ?? {}),
        },
        notes: notes?.trim() || null,
      })
      orderId = order.id
      orderNumber = order.order_number
    } catch (error) {
      // Nothing was created, so the basket keeps everything that can still be bought and
      // the shopper can try again. A product withdrawn while the basket sat in the browser
      // is the likely objection, and the server names the lines it refused -- so they are
      // taken out here rather than left for the shopper to find by trial and error.
      for (const productId of withdrawnProductIds(error)) remove(productId)
      setFormError(storefrontAuthMessage(error))
      return
    }

    // Leave this screen *before* emptying the basket, and only then empty it. The empty
    // basket is what the guard above bounces on, and a shopper who has just placed a real
    // order must not be sent back to an empty one.
    placed.current = true

    if (!settings?.payments_enabled) {
      // The deployment is on the simulated provider. Say the order is placed rather than
      // offering a pay button that cannot work; somebody will follow it up by hand.
      toast.success(`Order ${orderNumber} placed. We will be in touch to arrange payment.`)
      navigate(`/store/orders/${orderId}`, { replace: true })
      clear()
      return
    }

    try {
      const session = await startStorefrontCheckout(orderId)
      clear()
      // A full navigation, not `navigate()`: the card page is the processor's, on its own
      // origin, and `replace` so the browser's Back button returns to the shop rather than
      // re-submitting this form.
      window.location.replace(session.url)
    } catch (error) {
      // The order is real and unpaid. Send the shopper to it, where "Pay now" retries.
      toast.error(`Order ${orderNumber} was placed, but the card page could not be opened.`)
      setFormError(storefrontAuthMessage(error))
      navigate(`/store/orders/${orderId}`, { replace: true })
      clear()
    }
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Checkout
      </h1>

      <form onSubmit={onSubmit} noValidate className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Card>
            <CardHeader title="Delivery address" />
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Name" error={errors.name?.message}>
                  {(props) => <Input {...props} {...register('name')} autoComplete="name" />}
                </Field>
              </div>

              <Field
                label="Phone"
                error={errors.phone?.message ?? errors.dial_code?.message}
                hint="The courier calls this number."
              >
                {(props) => (
                  <div className="flex gap-2">
                    <select
                      className="h-10 w-28 shrink-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      aria-label="Dial code"
                      {...register('dial_code')}
                    >
                      {DIAL_CODES.map((d) => (
                        <option key={d.code} value={d.code}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <Input
                      {...props}
                      {...register('phone', {
                        // Strip anything that isn't a digit as it's typed, rather than
                        // waiting for blur to say the letters someone just typed are invalid.
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/\D/g, '').slice(0, 14)
                        },
                      })}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      className="flex-1"
                    />
                  </div>
                )}
              </Field>

              <Field label="Postcode" error={errors.postal_code?.message}>
                {(props) => (
                  <Input
                    {...props}
                    {...register('postal_code', {
                      // Strip anything that isn't a letter, digit, space or hyphen as it's
                      // typed -- covers UK-style alphanumeric postcodes too, not just digits.
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10)
                      },
                    })}
                    autoComplete="postal-code"
                  />
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label="Address" error={errors.line1?.message}>
                  {(props) => (
                    <Input
                      {...props}
                      {...register('line1')}
                      autoComplete="address-line1"
                      placeholder="Flat, house number, street"
                    />
                  )}
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label="Area" error={errors.line2?.message} hint="Optional.">
                  {(props) => (
                    <Input
                      {...props}
                      {...register('line2')}
                      autoComplete="address-line2"
                      placeholder="Landmark, area"
                    />
                  )}
                </Field>
              </div>

              <Field label="Town or city" error={errors.city?.message}>
                {(props) => (
                  <Input {...props} {...register('city')} autoComplete="address-level2" />
                )}
              </Field>

              <Field label="State" error={errors.state?.message}>
                {(props) => (
                  <Input {...props} {...register('state')} autoComplete="address-level1" />
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label="Country" error={errors.country?.message}>
                  {(props) => (
                    <Input {...props} {...register('country')} autoComplete="country-name" />
                  )}
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field
                  label="Delivery notes"
                  error={errors.notes?.message}
                  hint="Optional — anything the driver should know."
                >
                  {(props) => <Input {...props} {...register('notes')} />}
                </Field>
              </div>

              <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={locating}
                  disabled={pin !== null}
                  onClick={shareLocation}
                >
                  {pin ? 'Location shared' : 'Share my location'}
                </Button>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Optional. Helps us ship from the store or warehouse nearest you. Otherwise we go
                  by your postcode.
                </span>
                {locateError && (
                  <span className="w-full text-xs text-amber-700 dark:text-amber-400">
                    {locateError}
                  </span>
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-3">
          <Card>
            <CardHeader
              title="Your order"
              description={`${lines.length} item${lines.length === 1 ? '' : 's'}`}
            />
            <CardBody className="space-y-2 text-sm">
              {lines.map((line) => (
                <div key={line.product_id} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-slate-600 dark:text-slate-400">
                    {line.quantity} × {line.name}
                  </span>
                  <span className="shrink-0 text-slate-900 dark:text-slate-100">
                    {money(Number(line.price) * line.quantity)}
                  </span>
                </div>
              ))}

              <div className="space-y-2 border-t border-slate-200 pt-2 dark:border-slate-800">
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Tax</span>
                  <span className="text-slate-900 dark:text-slate-100">{money(tax)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Delivery</span>
                  <span className="text-slate-900 dark:text-slate-100">
                    {shipping.amount > 0 ? money(shipping.amount) : 'Free'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between border-t border-slate-200 pt-2 dark:border-slate-800">
                  <span className="font-medium text-slate-900 dark:text-slate-100">Estimated</span>
                  <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {money(goods + tax + shipping.amount)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                The exact amount is confirmed on the next screen, before you pay.
              </p>
            </CardBody>
          </Card>

          <Button type="submit" className="w-full" loading={isSubmitting}>
            {isSubmitting
              ? 'Placing your order…'
              : settings?.payments_enabled
                ? 'Place order and pay'
                : 'Place order'}
          </Button>

          {!settings?.payments_enabled && (
            <Alert tone="info">
              Card payments are not switched on for this shop yet. Your order will be placed and we
              will contact you to arrange payment.
            </Alert>
          )}
        </div>
      </form>
    </div>
  )
}
